import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { DateTime } from "luxon";
import { convertAmmountToWords } from "services";

export const generateSaleCertificate25PerPDF = async (
  eventData: any,
  highestBid: any,
  bidders: any[],
  bank?: any
) => {
  // === 1. Data prep (same as your code, just kept as-is) ===

  const bidderEmail = highestBid?.meta?.email || highestBid?.user_email;
  const highestBidder = bidders.find(
    (b) => b.email === bidderEmail || b.user_uuid === highestBid?.user_uuid
  );

  const bidderName =
    highestBidder?.meta?.name ||
    highestBid?.meta?.name ||
    highestBid?.bidder_name ||
    "N/A";

  const bidderAddress = highestBidder?.meta?.user_details?.address || "";
  const addressLines = bidderAddress ? bidderAddress.split("\n") : [];
  const addressLine1 = addressLines[0] || "";
  const addressLine2 = addressLines.slice(1).join(", ") || "";

  const eventId = eventData?.listing_id || "N/A";
  const borrowerName = eventData?.borrower_name || "N/A";
  const bankName =
    eventData?.property_dump?.bank?.name || eventData?.bank_name || "N/A";
  const propertyDescription =
    eventData?.property_dump?.sub_asset_type?.name ||
    eventData?.property_type ||
    "N/A";
  const auctionDate = eventData?.start_date_time
    ? DateTime.fromISO(eventData.start_date_time)
        .setZone("Asia/Kolkata")
        .toFormat("dd/MM/yyyy")
    : "N/A";
  const finalBidAmount = highestBid?.final_amount
    ? `₹${parseFloat(highestBid.final_amount).toLocaleString("en-IN")}`
    : "N/A";

  const currentDate = DateTime.now()
    .setZone("Asia/Kolkata")
    .toFormat("dd/MM/yyyy");

  const noOfDaysFor25Percent = eventData?.no_of_days_for_25_percent || 7;

  // Calculate days remaining from final_end_date + 15 days
  let noOfDaysForBalance = 0;
  if (eventData?.final_end_date) {
    const finalEndDate = DateTime.fromMillis(
      typeof eventData.final_end_date === "string"
        ? parseInt(eventData.final_end_date)
        : eventData.final_end_date
    ).setZone("Asia/Kolkata");

    // Add 15 days to final_end_date
    const deadlineDate = finalEndDate.plus({ days: 15 });

    // Calculate days remaining from today
    const today = DateTime.now().setZone("Asia/Kolkata").startOf("day");
    const deadline = deadlineDate.startOf("day");

    const daysDiff = deadline.diff(today, "days").days;
    noOfDaysForBalance = Math.max(0, Math.ceil(daysDiff));
  }
  const amount25Percent = highestBid?.final_amount * 0.25;
  const amount25PercentInWords = convertAmmountToWords(amount25Percent);

  const bankLogoUrl = bank?.logo?.key_path
    ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bank.logo.key_path}`
    : null;

  // === 2. Create hidden A4-like container ===
  // A4 @ ~96dpi ≈ 794 × 1123 px. We’ll use 794 and add padding inside.
  const tempDiv = document.createElement("div");
  tempDiv.style.position = "fixed";
  tempDiv.style.left = "-9999px";
  tempDiv.style.top = "0";
  tempDiv.style.width = "794px"; // content width for A4
  tempDiv.style.minHeight = "1123px"; // full A4 height
  //   tempDiv.style.padding = "60px 70px"; // visual margins inside the canvas
  tempDiv.style.boxSizing = "border-box";
  tempDiv.style.fontFamily = "'Times New Roman', serif";
  tempDiv.style.fontSize = "11pt";
  tempDiv.style.lineHeight = "1.5";
  tempDiv.style.color = "#000";
  tempDiv.style.backgroundColor = "#ffffff";

  tempDiv.innerHTML = `
    <!-- Header -->
    <div style="width: 100%; margin-bottom: 24px;">
      <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
        <div style="display: flex; justify-content: flex-start; align-items: center; flex: 1;">
          ${
            bankLogoUrl
              ? `<img src="${bankLogoUrl}" alt="Bank Logo" style="max-width: 180px; max-height: 60px; object-fit: contain;" />`
              : ""
          }
        </div>
      </div>
      <div style="display: flex; width: 100%; margin-top: 8px;">
        <div style="flex-basis: 50%; height: 3px; background-color: #F27E1F;"></div>
        <div style="flex-basis: 50%; height: 3px; background-color: #e5e7eb;"></div>
      </div>
    </div>

    <!-- Title -->
    <div style="text-align: center; font-weight: bold; font-size: 14pt; margin-bottom: 28px; text-decoration: underline;">
     Sale Confirmation Letter (25% Payment)
    </div>

    <!-- Date -->
    <div style="text-align: right; margin-bottom: 24px; font-size: 11pt;">
      Date: ${currentDate}
    </div>

    <!-- Address block -->
    <div style="margin-bottom: 24px; font-size: 11pt;">
      <div style="margin-bottom: 6px;">To,</div>
      <div style="margin-bottom: 4px;"><strong>${bidderName}</strong></div>
      ${
        addressLine1
          ? `<div style="margin-bottom: 4px;">${addressLine1}</div>`
          : ""
      }
      ${
        addressLine2
          ? `<div style="margin-bottom: 4px;">${addressLine2}</div>`
          : ""
      }
    </div>

    <div style="margin-bottom: 16px;">Dear Sir/Madam,</div>

    <!-- Subject -->
    <div style="font-weight: bold; margin-bottom: 20px; text-align: justify;">
      Sub: Confirmation of sale on receipt of 25% of bid amount – ${propertyDescription} – Event ID ${eventId}
    </div>

    <!-- Meta -->

    <div style="margin-bottom: 8px; text-align: justify;">
      <strong>Borrower Name:</strong> ${borrowerName}
    </div>

    <div style="margin-bottom: 8px; text-align: justify;">
      <strong>Auction Date:</strong> ${auctionDate}
    </div>

    <div style="margin-bottom: 20px; text-align: justify;">
      <strong>Final Bid:</strong> ${finalBidAmount}
    </div>

    <!-- Body paragraphs -->
    <div style="margin-bottom: 12px; text-align: justify;">
      We acknowledge receipt of 25% of the bid amount (inclusive of EMD) amounting to <b>${amount25Percent}</b> (Rupees <b style="text-transform: capitalize;">${amount25PercentInWords}</b>).
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
      Accordingly, subject to the terms and conditions of the sale notice and the provisions of the SARFAESI Act and Rules thereunder, the sale in your favour is hereby confirmed, subject to payment of the balance 75% of the sale consideration within the stipulated time.
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
     You are advised to arrange payment of the balance amount within ${noOfDaysForBalance} days from the date of this letter, failing which the sale is liable to be cancelled and the amounts paid may be dealt with in accordance with the terms of the auction and applicable law.
    </div>

    <div style="margin-bottom: 12px; text-align: justify;">
      This confirmation is issued without prejudice to the rights of the Secured Creditor and subject to final verification of documents and compliance with all formalities.
    </div>

    <!-- Footer -->
    <div style="margin-top: 50px; font-size: 11pt;">
      <div style="margin-bottom: 6px;">Yours faithfully,</div>
      <div style="margin-top: 40px;">
        <div style="margin-bottom: 4px;"><strong>Authorised Officer / Authorised Signatory</strong></div>
        <div style="margin-bottom: 4px;"><strong>${bankName}</strong></div>
        <div>Through AuctionBazaar.com platform</div>
      </div>
    </div>
  `;

  document.body.appendChild(tempDiv);

  // Wait for layout
  await new Promise((resolve) => setTimeout(resolve, 50));

  try {
    // === 3. Capture with html2canvas using the real width of the element ===
    const canvas = await html2canvas(tempDiv, {
      scale: 2, // higher resolution
      useCORS: true,
      logging: false,
      width: tempDiv.offsetWidth,
      height: tempDiv.offsetHeight,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(tempDiv);

    // === 4. Create PDF with margins (A4) ===
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // 210
    const pageHeight = pdf.internal.pageSize.getHeight(); // 297
    const marginLeftRight = 15; // mm
    const marginTop = 15; // mm

    const imgWidth = pageWidth - marginLeftRight * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const imgData = canvas.toDataURL("image/png");

    let heightLeft = imgHeight;
    let position = marginTop;

    pdf.addImage(
      imgData,
      "PNG",
      marginLeftRight,
      position,
      imgWidth,
      imgHeight
    );
    heightLeft -= pageHeight - marginTop;

    // Extra pages if content runs over (shouldn’t in your case, but safe)
    while (heightLeft > 0) {
      pdf.addPage();
      position = marginTop - (imgHeight - heightLeft);
      pdf.addImage(
        imgData,
        "PNG",
        marginLeftRight,
        position,
        imgWidth,
        imgHeight
      );
      heightLeft -= pageHeight - marginTop;
    }

    const filename = `Sale_Certificate_25_Percent_${eventId}_${DateTime.now().toFormat(
      "yyyyMMdd"
    )}.pdf`;

    pdf.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    if (document.body.contains(tempDiv)) {
      document.body.removeChild(tempDiv);
    }
    throw error;
  }
};
